import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ReportsData } from "@/hooks/useReportsData";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import { format } from "date-fns";
import { MessageCircle, TrendingUp, Lightbulb, Target } from "lucide-react";

interface ChatsAnalyticsProps {
  data: ReportsData;
  compact?: boolean;
}

const ChatIcon = ({ className }: { className?: string }) => (
  <svg
    viewBox="0 0 256 256"
    className={className}
    aria-hidden="true"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fill="currentColor"
      d="M128,20A108,108,0,0,0,31.85,177.23L21,209.66A20,20,0,0,0,46.34,235l32.43-10.81A108,108,0,1,0,128,20Zm0,192a84,84,0,0,1-42.06-11.27,12,12,0,0,0-6-1.62,12.1,12.1,0,0,0-3.8.62l-29.79,9.93,9.93-29.79a12,12,0,0,0-1-9.81A84,84,0,1,1,128,212Z"
    />
  </svg>
);

export const ChatsAnalytics: React.FC<ChatsAnalyticsProps> = ({
  data,
  compact = false,
}) => {
  const totalChats = data.chatStats?.totalChats || 0;
  const totalMessages = data.chatStats?.totalMessages || 0;
  const avgMessagesPerChat = data.chatStats?.avgMessagesPerChat || 0;
  const chatActivityData = data.chatStats?.activityByDay || [];
  
  // Most active topics from chat titles
  const topTopics = data.chatStats?.topTopics || [];
  
  // Chat engagement insights
  const engagementScore = totalChats > 0 
    ? Math.min(100, Math.round((avgMessagesPerChat / 10) * 100))
    : 0;

  const chartConfig = {
    messages: { label: "Messages", color: "hsl(var(--chart-1))" },
    chats: { label: "Chats", color: "hsl(var(--chart-2))" },
  };

  if (totalChats === 0) {
    return (
      <Card className="border-border bg-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <ChatIcon className="h-5 w-5" />
            Chats
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex h-48 items-center justify-center text-center">
            <div>
              <ChatIcon className="mx-auto mb-3 h-12 w-12 text-muted-foreground/50" />
              <p className="text-muted-foreground">
                Start a chat conversation to track your learning progress.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (compact) {
    return (
      <Card className="border-border bg-card">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-lg">
            <ChatIcon className="h-5 w-5" />
            Chats
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Chats</span>
              <span className="font-medium text-foreground">{totalChats}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Messages</span>
              <span className="font-medium text-foreground">{totalMessages}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Avg per Chat</span>
              <span className="font-medium text-foreground">{avgMessagesPerChat}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Engagement</span>
              <span className="font-medium text-foreground">{engagementScore}%</span>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="border-border bg-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-primary/10 p-2">
                <ChatIcon className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Chats</p>
                <p className="text-xl font-semibold">{totalChats}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border bg-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-info/10 p-2">
                <MessageCircle className="h-5 w-5 text-info" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Messages</p>
                <p className="text-xl font-semibold">{totalMessages}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border bg-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-correct/10 p-2">
                <TrendingUp className="h-5 w-5 text-correct" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Avg Messages/Chat</p>
                <p className="text-xl font-semibold">{avgMessagesPerChat}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border bg-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-premium/10 p-2">
                <Target className="h-5 w-5 text-premium" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Engagement Score</p>
                <p className="text-xl font-semibold">{engagementScore}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Chat Activity Trend */}
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="text-lg">Chat Activity (Last 30 Days)</CardTitle>
          </CardHeader>
          <CardContent>
            {chatActivityData.length > 0 ? (
              <ChartContainer config={chartConfig} className="h-48 w-full">
                <AreaChart data={chatActivityData}>
                  <XAxis
                    dataKey="date"
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 12 }}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 12 }}
                  />
                  <ChartTooltip content={<ChartTooltipContent />} />
                  <Area
                    type="monotone"
                    dataKey="messages"
                    stroke="hsl(var(--chart-1))"
                    fill="hsl(var(--chart-1))"
                    fillOpacity={0.2}
                    strokeWidth={2}
                  />
                </AreaChart>
              </ChartContainer>
            ) : (
              <div className="flex h-48 items-center justify-center">
                <p className="text-sm text-muted-foreground">No activity data yet</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Learning Insights */}
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Lightbulb className="h-5 w-5 text-warning" />
              Learning Insights
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="rounded-lg border border-border bg-muted/30 p-4">
                <p className="text-sm font-medium">Chat Engagement</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {engagementScore >= 70
                    ? "Great engagement! You're actively using AI to enhance your learning."
                    : engagementScore >= 40
                    ? "Good start! Try asking more follow-up questions for deeper understanding."
                    : "Try having longer conversations with the AI for better learning outcomes."}
                </p>
              </div>
              <div className="rounded-lg border border-border bg-muted/30 p-4">
                <p className="text-sm font-medium">Conversation Depth</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {avgMessagesPerChat >= 8
                    ? "Excellent depth! Your conversations show thorough exploration of topics."
                    : avgMessagesPerChat >= 4
                    ? "Good conversations. Try asking for examples and clarifications."
                    : "Consider asking more questions to deepen your understanding."}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Top Topics */}
      {topTopics.length > 0 && (
        <Card className="border-border bg-card">
          <CardHeader>
            <CardTitle className="text-lg">Most Discussed Topics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {topTopics.slice(0, 6).map((topic, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between rounded-lg border border-border bg-muted/30 p-3"
                >
                  <span className="truncate font-medium">{topic.title}</span>
                  <span className="ml-2 text-sm text-muted-foreground">
                    {topic.messageCount} msgs
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};